import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Sparkles, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, pw);
      toast.success("Welcome back.");
      nav(loc.state?.from || "/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-ink text-white relative overflow-hidden grain">
        <Link to="/" className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl bg-white text-ink grid place-items-center"><Sparkles size={16} /></div>
          <span className="font-display font-bold text-lg">Zenith Ranking</span>
        </Link>
        <div>
          <div className="eyebrow !text-white/50 mb-3">Why teams choose us</div>
          <h2 className="font-display text-4xl font-bold tracking-tight">"Our pipeline coverage from organic doubled in three months. The reporting clarity is unmatched."</h2>
          <p className="mt-6 text-white/70 text-sm">Priya Raman · Marketing Lead, Saga SaaS</p>
        </div>
        <div className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-terra/30 blur-3xl" />
      </div>

      <div className="flex items-center justify-center p-8 sm:p-12">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-8 w-8 rounded-xl bg-ink text-white grid place-items-center"><Sparkles size={14} /></div>
            <span className="font-display font-bold">Zenith Ranking</span>
          </Link>
          <div className="eyebrow mb-3">Welcome back</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Sign in to Zenith Ranking</h1>
          <p className="mt-2 text-sm text-inkSoft">No account? <Link to="/signup" className="text-terra hover:underline">Create one</Link> · <Link to="/forgot-password" className="text-terra hover:underline" data-testid="forgot-password-link">Forgot password?</Link></p>

          <button onClick={googleLogin} type="button" className="mt-8 w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl border border-line text-sm font-medium hover:bg-surfaceAlt transition" data-testid="google-login-btn">
            <img src="https://www.gstatic.com/marketing-cms/assets/images/d5/dc/cfe9ce8b4425b410b49b7f2dd3f3/g.webp=s48-fcrop64=1,00000000ffffffff-rw" alt="" className="h-4 w-4" /> Continue with Google
          </button>
          <div className="my-6 flex items-center gap-3 text-xs text-inkSoft">
            <div className="flex-1 h-px bg-line" /> or email <div className="flex-1 h-px bg-line" />
          </div>
          <form onSubmit={submit} className="space-y-3" data-testid="login-form">
            <Input label="Email" type="email" value={email} onChange={setEmail} testid="login-email" />
            <Input label="Password" type="password" value={pw} onChange={setPw} testid="login-password" />
            <button type="submit" disabled={loading} className="btn-primary w-full justify-center mt-2" data-testid="login-submit">
              {loading ? "Signing in..." : (<>Sign in <ArrowUpRight size={16} /></>)}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function Input({ label, type = "text", value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-inkSoft">{label}</span>
      <input
        type={type}
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full h-11 px-4 rounded-xl border border-line bg-white text-sm outline-none focus:border-ink"
        data-testid={testid}
      />
    </label>
  );
}
